import React, { useState, useRef, useEffect } from "react";
import { View, Text, TextInput, StyleSheet, Platform, Image } from "react-native";
import { useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import { inputAssistDisabledProps } from "../../../common/FloatingTextInputComponent";
import WarningIcon from "../../../../../../../../assets/images/warning.png";

type Props = {
  onValidationChange?: (valid: boolean) => void;
  onContinue?: () => void;
  /** API error from confirm-PIN flow (e.g. update/validate) */
  apiError?: string;
  testID?: string | undefined;
};

export default function ConfirmPinInputStep({ onValidationChange, onContinue, apiError, testID }: Props) {
  const [pin, setPin] = useState(["", "", "", ""]);
  const { t } = useTranslation();
  const inputs = useRef<(TextInput | null)[]>([]);
  const originalPin = useSessionStore((s: any) => s.consumerPin);
  const [isMismatch, setIsMismatch] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isNewConsumer = useSessionStore((s: any) => (s as any).isNewConsumer);
  const suppressWebFocusRing = (e: any) => {
    if (Platform.OS !== "web") return;
    const el = e?.target as any;
    if (!el?.style) return;
    el.style.outline = "none";
    el.style.boxShadow = "none";
    el.style.webkitTapHighlightColor = "transparent";
  };

  const borderColor = isFocused
    ? Colors.black
    : Colors.textInputBackground;

  const borderWidth = isFocused ? 1 : 1;
  const showErrorBorder = isMismatch;
  const pinBorderColor = showErrorBorder ? Colors.errorRedColor : borderColor;
  const pinBorderWidth = isNewConsumer && !showErrorBorder ? 0 : borderWidth;

  const handleChange = (text: string, index: number) => {
    // Validate that text contains only digits using string methods (avoid ReDoS)
    // Check each character to ensure it's a digit (0-9) without using regex
    const isValid = text.length === 0 || text.split('').every(char => {
      const charCode = char.charCodeAt(0);
      return charCode >= 48 && charCode <= 57; // '0' is 48, '9' is 57
    });
    
    if (isValid) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);

      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }

      // if (newPin.every(d => d !== ""))  {
      //   console.log('confirm PIN CONTINUE====>', text, index)
      //   onContinue?.()
      // }
      const joined = newPin.join("");
      if (joined?.length === 4) {
        const mismatch = String(originalPin || "") !== joined;
        setIsMismatch(mismatch);

        if (!mismatch) {
          // ✅ Only call onContinue if pins match
          onContinue?.();
        }
      }
    }
  };

  useEffect(() => {
    const joined = pin.join("");
    const complete = joined.length === 4;
    const mismatch = complete && String(originalPin || "") !== joined;
    setIsMismatch(mismatch);
    if (onValidationChange) {
      onValidationChange(complete && !mismatch);
    }
  }, [pin, originalPin, onValidationChange]);

  useEffect(() => {
    requestAnimationFrame(() => {
      inputs.current[0]?.focus();
    });
  }, []);

  return (
    <View style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("PIN_CONFIRM_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("PIN_CONFIRM_SUBTITLE")}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <View style={[styles.pinBox, { borderColor: pinBorderColor, borderWidth: pinBorderWidth }]}>
            <TextInput
              key={index}
              testID={`confirmPinInput${index}`}
              ref={(el) => (inputs.current[index] = el)}
              autoFocus={index === 0}
              style={[styles.pinInput, { borderColor: pinBorderColor, borderWidth: pinBorderWidth }]}
              keyboardType="number-pad"
              maxLength={1}
              secureTextEntry
              value={digit}
              onChangeText={(text) => handleChange(text, index)}
              onFocus={(e: any) => {
                suppressWebFocusRing(e);
                setIsFocused(true);
              }}
              onBlur={() => setIsFocused(false)}
              {...inputAssistDisabledProps}
            />
          </View>
        ))}
      </View>

      {isMismatch && (
        <View style={{ flexDirection: 'row', alignItems: 'center'}}>
          <Image
            source={WarningIcon}
            style={{height: 16, width: 16, marginRight: 8}}
            resizeMode='contain'           
          />
        <Text style={styles.error}>{t("PIN_DOES_NOT_MATCH")}</Text>
      </View>
        
      )}
    </View>
  );
}

const styles = StyleSheet.create({
    container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  pinContainer: {
    flexDirection: "row",
    marginBottom: 8,
    justifyContent: 'space-between'
  },
  pinInput: {
    borderRadius: 8,
    flex: 1,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: Colors.searchCompanyBackground,
    ...(Platform.OS === 'web' && {
      outlineWidth: 0,
      outline: 'none',
      border: 'none',
      boxShadow: 'none',
    } as any),
  },
  pinBox: {
    width: 56,
    height: 56,
    borderRadius: 8,
    textAlign: "center",
  },
  error: {
    color: "#DA291C",
    fontSize: 14,
    fontWeight: '400',
    fontFamily: 'Rubik',
    lineHeight: 20
  },
  apiErrorContainer: {
    marginTop: 12,
    marginBottom: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
  },
  apiErrorText: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    color: '#B91C1C',
    lineHeight: 20,
  },
});
